import MainLayout from "../../layout/MainLayout";
import {
  Select,
  Button2,
  Button1,
  TagInput,
  QuickActionSelect,
} from "../index";
import plusIcon from "../../assets/icons/plus.png";
import downIcon from "../../assets/icons/chevron-down.png";
import refreshIcon from "../../assets/icons/refresh-icon.png";
import warningIcon from "../../assets/icons/warning-icon.png";
import { DatePicker, Input, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccounts } from "../../data/live";
import { conversionsApi } from "../../api/endpoints";
import { labelWithFlag } from "../../data/currencyFlags";

const last4 = (s) => (s ? String(s).slice(-4) : "0000");
const CATEGORIES = ["FX", "Trading", "Travel", "Other"];

const NewConversion = () => {
  const navigate = useNavigate();
  const { accounts, refresh } = useAccounts();
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [destAccountId, setDestAccountId] = useState("");
  const [sourceAmount, setSourceAmount] = useState("");
  const [conversionDate, setConversionDate] = useState(null);
  const [category, setCategory] = useState("");
  const [quote, setQuote] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!accounts.length) {
      setSourceAccountId("");
      setDestAccountId("");
      return;
    }

    const currentSource = accounts.find((a) => a.id === sourceAccountId);
    const src =
      currentSource ||
      accounts.find((a) => a.isDefault) ||
      accounts.find((a) => a.currency === "AUD") ||
      accounts[0];

    if (!currentSource) {
      setSourceAccountId(src.id);
    }

    const currentDest = accounts.find(
      (a) =>
        a.id === destAccountId &&
        a.id !== src.id &&
        a.currency !== src.currency
    );
    if (!currentDest) {
      const dst = accounts.find(
        (a) => a.id !== src.id && a.currency !== src.currency
      );
      setDestAccountId(dst?.id || "");
    }
  }, [accounts, sourceAccountId, destAccountId]);

  const sourceAccount = accounts.find((a) => a.id === sourceAccountId);
  const destAccount = accounts.find((a) => a.id === destAccountId);
  const sourceOptions = accounts.map((a) => ({
    name: `${labelWithFlag(a.currency)} • ****${last4(a.accountNumber || a.iban)}${
      a.isDefault ? " (Default)" : ""
    }`,
    value: a.id,
  }));

  // Destination must be a different currency from source (server requirement).
  const destOptions = accounts
    .filter(
      (a) =>
        a.id !== sourceAccountId &&
        (!sourceAccount || a.currency !== sourceAccount.currency)
    )
    .map((a) => ({
      name: `${labelWithFlag(a.currency)} • ${Number(a.availableBalance).toLocaleString()}`,
      value: a.id,
    }));

  const sourceCurrencyOptions = sourceOptions.map((option) => {
    const cur = accounts.find((account) => account.id === option.value)?.currency;
    return { ...option, name: labelWithFlag(cur) };
  });
  const destCurrencyOptions = destOptions.map((option) => {
    const cur = accounts.find((account) => account.id === option.value)?.currency;
    return { ...option, name: labelWithFlag(cur) };
  });

  const handleSourceAccountChange = (value) => {
    const src = accounts.find((a) => a.id === value);
    setSourceAccountId(value);
    if (!src) {
      setDestAccountId("");
      return;
    }
    const nextDest = accounts.find(
      (a) => a.id !== src.id && a.currency !== src.currency
    );
    setDestAccountId(nextDest?.id || "");
  };

  // Live indicative rate, fetches whenever both currencies are picked,
  // even before the user types an amount. So the page always shows
  // "1 AUD = X EUR" instead of 0.000000.
  const [indicativeRate, setIndicativeRate] = useState(null); // { midRate, effectiveRate, markupPercent }
  useEffect(() => {
    const src = sourceAccount?.currency;
    const dst = destAccount?.currency;
    if (!src || !dst || src === dst) {
      setIndicativeRate(null);
      return;
    }
    let cancelled = false;
    conversionsApi
      .rate(src, dst)
      .then((r) => {
        if (!cancelled) setIndicativeRate(r);
      })
      .catch(() => !cancelled && setIndicativeRate(null));
    return () => {
      cancelled = true;
    };
  }, [sourceAccount?.currency, destAccount?.currency]);

  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (
      !sourceAccountId ||
      !destAccountId ||
      !sourceAmount ||
      isNaN(Number(sourceAmount)) ||
      Number(sourceAmount) <= 0
    ) {
      setQuote(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      conversionsApi
        .quote({
          sourceAccountId,
          destAccountId,
          sourceAmount: Number(sourceAmount),
        })
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [sourceAccountId, destAccountId, sourceAmount]);

  // Mid-market rate to display: prefer live quote (Wise-style mid rate),
  // fall back to indicative midRate from the rate endpoint. Coerced to a finite
  // number so a missing/odd value renders as a placeholder, never "NaN".
  const displayRateNum = Number(
    quote?.midRate ?? quote?.rate ?? indicativeRate?.midRate
  );
  const displayRate =
    Number.isFinite(displayRateNum) && displayRateNum > 0 ? displayRateNum : null;

  const confirmConversion = async () => {
    setError(null);
    if (!sourceAccountId || !destAccountId) {
      setError("Select source and destination accounts");
      return;
    }
    if (!quote || !quote.hasSufficientBalance) {
      setError("Insufficient balance or quote unavailable");
      return;
    }
    setConfirming(true);
    try {
      await conversionsApi.create({
        sourceAccountId,
        destAccountId,
        sourceAmount: Number(sourceAmount),
      });
      message.success("Conversion completed");
      await refresh();
      navigate("/wallet");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Conversion failed");
    } finally {
      setConfirming(false);
    }
  };

  const sourceAvail = sourceAccount
    ? `${Number(sourceAccount.availableBalance).toLocaleString()} ${
        sourceAccount.currency
      }`
    : "0.00";

  const destAvail = destAccount
    ? `${Number(destAccount.availableBalance).toLocaleString()} ${
        destAccount.currency
      }`
    : "0.00";

  return (
    <>
      <MainLayout
        expand={false}
        NavName="Convert"
        navbarChildren={<QuickActionSelect className="change-down-icon-white" />}
        pageClassName="dashboard-page finance-action-page convert-page"
        contentClassName="dashboard-content"
      >
        <div className="finance-action-shell">
            <div className="finance-action-overview">
              <div>
                <span className="dashboard-eyebrow">Currency conversion</span>
                <h2>Convert Funds</h2>
                <p>Exchange between wallet currencies with live indicative rates and transparent conversion details.</p>
              </div>
              <div className="d-flex gap-3 align-items-center flex-wrap justify-content-md-end">
                <p className="p-0 m-0 fs14px">
                  Available balance:{" "}
                  <span
                    className="fs-6 fw-medium"
                    style={{ color: "#1bbb6b" }}
                  >
                    {sourceAvail}
                  </span>
                </p>
                <Button2
                  buttonName={"Add Funds"}
                  className={"btn-outline-success"}
                  divClassName={"gap-1"}
                  icon={<img src={plusIcon} alt="add" />}
                  onClick={() => navigate("/add-funds")}
                />
              </div>
            </div>

            <div className="finance-action-toolbar d-flex flex-column gap-3 mt-4">
              <Select
                value={sourceAccountId}
                onChange={(e) => handleSourceAccountChange(e.target.value)}
                options={sourceOptions}
                defaultValue="Select account"
                selectStyle={{ width: "100%", maxWidth: "320px" }}
                className="bg-light rounded-5"
                popupClassName="bg-White"
              />
              <div className="d-flex  justify-content-md-between flex-wrap justify-content-center gap-2">
                <span className="fs-5 fw-semibold">Conversion</span>
              </div>
            </div>

            <div className="finance-action-card row mt-4 justify-content-between p-3">
              <div className="col-lg-5 mb-2 p-3">
                <span className="fs-5 fw-medium">New Conversion</span>
                <div className="d-flex flex-column gap-3 mt-4">
                  <div className="d-flex flex-column gap-2">
                    <span className="fs12px " style={{ color: "#7D7D7D" }}>
                      Exchange rate
                    </span>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="fw-medium">
                        1 {sourceAccount?.currency ? labelWithFlag(sourceAccount.currency) : "-"} ={" "}
                        {displayRate
                          ? Number(displayRate).toFixed(6)
                          : "-"}{" "}
                        {destAccount?.currency ? labelWithFlag(destAccount.currency) : "-"}
                      </span>
                      <img
                        src={downIcon}
                        alt="down"
                        className="cursor-pointer"
                      />
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <span className="fs12px" style={{ color: "#7D7D7D" }}>
                        {quote
                          ? "Guaranteed mid-market rate"
                          : indicativeRate
                          ? "Indicative mid-market rate"
                          : !destAccount
                          ? "Create or select another currency wallet"
                          : sourceAccount?.currency === destAccount?.currency
                          ? "Pick two different currencies"
                          : "Loading rate..."}
                      </span>
                      <img
                        src={refreshIcon}
                        alt="refresh"
                        className="cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <div className="row justify-content-between">
                      <div className="col-9">
                        <label className="fs12px">From (Amount)</label>
                        <Input
                          value={sourceAmount}
                          onChange={(e) => setSourceAmount(e.target.value)}
                          placeholder="0.00"
                          className="p-3"
                        />
                      </div>
                      <div className="col-3">
                        <label className="fs12px">Currency</label>
                        <Select
                          value={sourceAccountId}
                          options={sourceCurrencyOptions}
                          onChange={(e) =>
                            handleSourceAccountChange(e.target.value)
                          }
                          defaultValue="From"
                          className="p-3"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <div className="row justify-content-between">
                      <div className="col-9">
                        <label className="fs12px">To (Amount)</label>
                        <Input
                          value={
                            quote?.destAmount
                              ? Number(quote.destAmount).toFixed(2)
                              : ""
                          }
                          readOnly
                          placeholder="0.00"
                          className="p-3"
                        />
                      </div>
                      <div className="col-3">
                        <label className="fs12px">Currency</label>
                        <Select
                          value={destAccountId}
                          options={destCurrencyOptions}
                          onChange={(e) => setDestAccountId(e.target.value)}
                          defaultValue="To"
                          className="p-3"
                          disabled={!destOptions.length}
                        />
                      </div>
                    </div>
                  </div>

                  {!destOptions.length && (
                    <div
                      className="p-3 rounded-3 fs12px"
                      style={{
                        color: "#636363",
                        backgroundColor: "rgba(27, 187, 107, 0.06)",
                        border: "1px solid rgba(27, 187, 107, 0.2)",
                      }}
                    >
                      You need another wallet in a different currency before a
                      conversion can be created.
                    </div>
                  )}

                  <div className="d-flex flex-column gap-2">
                    <label className="fs12px">Conversion date</label>
                    <DatePicker
                      className="p-3"
                      onChange={(d) =>
                        setConversionDate(d?.toISOString() || null)
                      }
                    />
                    <span className="fs12px" style={{ color: "#7D7D7D" }}>
                      Choose the date you would like ARM Financial to process this
                      conversion
                    </span>
                  </div>
                </div>
              </div>

              <div className="col-lg-6 mb-2 p-3">
                <span className="fs-5 fw-medium">Summary</span>
                <div className="d-flex flex-column gap-3 mt-4">
                  <div className="d-flex flex-column gap-2">
                    <span className="fs12px " style={{ color: "#7D7D7D" }}>
                      From amount
                    </span>

                    <div className="d-flex gap-2 align-items-center">
                      <span className="fw-medium">
                        {Number(sourceAmount || 0).toLocaleString()}{" "}
                        {sourceAccount?.currency || ""}
                      </span>
                      <span
                        className="fw-medium fs12px ms-3"
                        style={{ color: "#7D7D7D" }}
                      >
                        Available balance
                      </span>
                      <span className="fs12px fw-semibold">{sourceAvail}</span>
                    </div>
                  </div>

                  {/* Wise-style transparent breakdown */}
                  {quote && (
                    <div
                      className="d-flex flex-column gap-2 p-3 rounded-3"
                      style={{ backgroundColor: "rgba(27, 187, 107, 0.05)", border: "1px solid rgba(27, 187, 107, 0.25)" }}
                    >
                      <span className="fs12px fw-semibold" style={{ color: "#1bbb6b" }}>
                        Transfer details
                      </span>
                      <div className="d-flex justify-content-between fs12px">
                        <span style={{ color: "#7D7D7D" }}>You convert exactly</span>
                        <span className="fw-bold">
                          {Number(quote.sourceAmount).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {quote.sourceCurrency}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between fs12px">
                        <span style={{ color: "#7D7D7D" }}>Total fees (included)</span>
                        <span className="fw-medium">
                          {Number(quote.exchangeFee ?? 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {quote.sourceCurrency}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between fs12px">
                        <span style={{ color: "#7D7D7D" }}>Total amount we&apos;ll convert</span>
                        <span className="fw-medium">
                          {Number(quote.amountToConvert ?? quote.sourceAmount - (quote.exchangeFee ?? 0)).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {quote.sourceCurrency}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between fs12px">
                        <span style={{ color: "#7D7D7D" }}>Guaranteed rate</span>
                        <span className="fw-medium">
                          1 {quote.sourceCurrency} ={" "}
                          {Number(quote.midRate ?? quote.rate ?? 0).toFixed(6)}{" "}
                          {quote.destCurrency}
                        </span>
                      </div>
                      <hr className="m-0 opacity-25" />
                      <div className="d-flex justify-content-between fs12px">
                        <span className="fw-semibold">You receive</span>
                        <span className="fw-bold" style={{ color: "#1bbb6b" }}>
                          {Number(quote.destAmount).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{" "}
                          {quote.destCurrency}
                        </span>
                      </div>
                    </div>
                  )}

                  <div className="d-flex flex-column gap-2">
                    <span className="fs12px " style={{ color: "#7D7D7D" }}>
                      To amount
                    </span>

                    <div className="d-flex gap-2 align-items-center">
                      <span className="fw-medium">
                        {quote?.destAmount
                          ? Number(quote.destAmount).toFixed(2)
                          : "0.00"}{" "}
                        {destAccount?.currency || ""}
                      </span>
                      <span
                        className="fw-medium fs12px ms-3"
                        style={{ color: "#7D7D7D" }}
                      >
                        Available balance
                      </span>
                      <span className="fs12px fw-semibold">{destAvail}</span>
                    </div>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    <span className="fs12px" style={{ color: "#7d7d7d" }}>
                      Conversion date
                    </span>
                    <span className="fw-medium">
                      {conversionDate
                        ? new Date(conversionDate).toLocaleDateString()
                        : "Today"}
                    </span>
                  </div>

                  <div className="mt-4 d-flex flex-column gap-3">
                    <span className="fs-5 fw-bold">Additional Details</span>
                    <div className="d-flex flex-column gap-2 ">
                      <label className="fs12px ">Category</label>
                      <Select
                        value={category}
                        options={CATEGORIES.map((c) => ({
                          name: c,
                          value: c,
                        }))}
                        defaultValue="Choose Category"
                        className="rounded-2 p-3"
                        onChange={(e) => setCategory(e.target.value)}
                      />
                    </div>
                    <div className="d-flex flex-column gap-2 ">
                      <label className="fs12px ">Tags</label>
                      <TagInput
                        className="border py-3"
                        inputBgColor="transparent"
                        tagColor="#1bbb6b"
                        tagTextColor="#000"
                        limit={10}
                        placeholder="Add up to 10 Tags"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="d-flex gap-3 rounded-3 p-3 px-4 align-items-center"
                style={{ backgroundColor: "#F7F7F7" }}
              >
                <img src={warningIcon} alt="warning" />
                <span className="fw-medium">
                  Once confirmed, the conversion cannot be cancelled.
                </span>
              </div>

              {quote && !quote.hasSufficientBalance && (
                <div className="col-12 p-2">
                  <span className="fs12px" style={{ color: "#FC555B" }}>
                    Insufficient balance in source account
                  </span>
                </div>
              )}
              {error && (
                <div className="col-12 p-2">
                  <span className="fs12px" style={{ color: "#FC555B" }}>
                    {error}
                  </span>
                </div>
              )}

              <div className="d-flex justify-content-md-end justify-content-between mt-4 p-2 gap-3">
                <Button1
                  buttonName={"Save as Draft"}
                  className={"btn-outline-success"}
                />
                <Button1
                  buttonName={
                    confirming ? "Converting..." : "Confirm Conversion"
                  }
                  className="btn-dark "
                  onClick={confirmConversion}
                  disabled={
                    confirming || !quote?.hasSufficientBalance || !destAccount
                  }
                />
              </div>
            </div>
        </div>
      </MainLayout>
    </>
  );
};

export default NewConversion;
